'use client';
import { Theme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { gsap } from 'gsap';
import { useRef, useEffect } from 'react';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import Container from '@repo/ui/src/components/Section';
import useWindowSize from '@components/shared/window-size.jsx';
import { imageFullWidthSmAndUp, imageFullWidthXs } from '@style/variables';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import BackgroundVideo from '@repo/ui/src/components/BackgroundVideo';
import { MediaType } from '@repo/ui/src/types/image';

import {
  Section,
  Image,
  VerticalCarouselContent,
  VerticalCarouselGalleryItem,
  VerticalCarouselGallery,
  VerticalCarouselItem,
  VideoWrapper,
  CarouselOverlay,
  VerticalCarouselItemsContainer,
} from './styles';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';

interface VerticalProps {
  galleryImages: Array<MediaType>;
  carouselCards: Array<{
    textAlignRight: boolean;
    toutContent: ToutContentProps;
  }>;
  alignRight: boolean;
  _testID?: string;
}

const VerticalCarousel = ({ galleryImages, carouselCards, _testID }: VerticalProps) => {
  const xsOnly = useMediaQuery((theme: Theme) => theme.breakpoints.only('xs'));
  const ref = useRef<HTMLElement>(null);
  const [width] = useWindowSize();
  const vidWidth = width > 1920 ? 1920 : width;

  useEffect(() => {
    const element = ref.current;
    if (element) {
      gsap.set(`.vertical-carousel-gallery-item-0`, { opacity: 1 });

      carouselCards
        .map((_, index) => {
          const itemAnimation = gsap.timeline({
            scrollTrigger: {
              trigger: `.carousel-item-start-${index}`,
              start: 'top bottom',
              end: 'top bottom',
              onEnter: () => {
                gsap.fromTo(
                  `.vertical-carousel-gallery-item-${index}`,
                  {
                    opacity: index === 0 ? 1 : 0,
                    scale: index === 0 ? 1 : 1.7,
                  },
                  { opacity: 1, scale: 1 },
                );
              },
              onLeaveBack: () => {
                gsap.to(`.vertical-carousel-gallery-item-${index}`, {
                  opacity: index === 0 ? 1 : 0,
                });
              },
              onEnterBack: () => {
                gsap.fromTo(
                  `.vertical-carousel-gallery-item-${index}`,
                  {
                    scale: 1,
                  },
                  { scale: 1.7 },
                );
              },
            },
          });

          const zoomAnimation = gsap.timeline({
            scrollTrigger: {
              trigger: `.carousel-content-${index}`,
              start: 'center bottom',
              end: 'center top',
              onToggle: ({ isActive }) => {
                return gsap.fromTo(
                  '.carousel-overlay',
                  { opacity: 0 },
                  { opacity: 0.7, duration: 0.6, reversed: !isActive },
                );
              },
            },
          });

          return [itemAnimation, zoomAnimation];
        })
        .flat();

      gsap.timeline({
        scrollTrigger: {
          trigger: `.vertical-carousel-gallery-item-0`,
          start: 'top top',
          onLeaveBack: () => {
            gsap.set(`.carousel-overlay`, { opacity: 0 });
          },
        },
      });
    }
  }, []);

  return (
    <Section ref={ref} className="vertical-carousel">
      <Container dark gutterX gutterY zIndexNone>
        <VerticalCarouselGallery
          className={`vertical-carousel-gallery`}
          data-test-id={`vertical_carousel_gallery_${_testID}`}
        >
          {galleryImages.map((galleryImage, i) => (
            <VerticalCarouselGalleryItem key={i} className={`vertical-carousel-gallery-item-${i}`}>
              {galleryImage?._meta?.schema.includes('image-link') ? (
                <Image
                  imgComponent={
                    <img
                      src={buildImageSrc(
                        galleryImage,
                        xsOnly ? imageFullWidthXs : imageFullWidthSmAndUp,
                        0,
                        0,
                      )}
                      role="presentation"
                      alt=""
                    />
                  }
                />
              ) : (
                <VideoWrapper>
                  <BackgroundVideo
                    video={galleryImage}
                    width={vidWidth}
                    fillContainer
                    className={`product-video`}
                  />
                </VideoWrapper>
              )}
            </VerticalCarouselGalleryItem>
          ))}
          <CarouselOverlay className="carousel-overlay" />
        </VerticalCarouselGallery>
        <VerticalCarouselItemsContainer>
          {carouselCards.map((carouselCard, i) => (
            <div className={`carousel-item-start-${i}`} key={i}>
              <VerticalCarouselItem
                className={`vertical-carousel-item carousel-content-${i}`}
                textAlignRight={carouselCard.textAlignRight}
              >
                <VerticalCarouselContent className="carousel-content">
                  <ToutContent
                    divider
                    dark
                    align="left"
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    {...carouselCard.toutContent}
                    headingStyleVariant="h2"
                    className="card-tout-content"
                  />
                </VerticalCarouselContent>
              </VerticalCarouselItem>
            </div>
          ))}
        </VerticalCarouselItemsContainer>
      </Container>
    </Section>
  );
};

export default VerticalCarousel;
